import { produce } from "immer";
import type { StateCreator } from "zustand";

import { setupWorkerMessageHandler } from "../../main-canvas/messages/handle-track-messages.ts";
import { refreshTracks } from "../../main-canvas/messages/post-track-messages.ts";
import { refreshMain } from "../../main-canvas/render-main.ts";
import { AlignmentDensityEnum, type components, PileupRenderingModeEnum, type SyntenyTypeEnum } from "../../openapi";
import { MIN_FOCUSED_SHAPE_WIDTH } from "../../settings.ts";
import type { MiniFamily } from "../../synteny-worker/state.ts";
import {
  type AlignmentDensity,
  type CanvasCoordinate,
  type MainThreadHoverMessage,
  type NavigationFeature,
  type OrthologousGroup,
  type PileupRenderingMode,
  type RenderingOptions,
  type Track,
  type TrackGroup,
  type TrackGroups,
  type Tracks,
} from "../../types.ts";
import type { FetchError } from "../../utils/errors.ts";
import { createNewTracks, type TrackConfig } from "../../utils/tracks/create-tracks.ts";
import { removeTracks } from "../../utils/tracks/remove-tracks.ts";
import { getTrackTypeBySyntenyType } from "../../utils/type-conversion.ts";
import { createTracksLoader } from "../load-tracks.ts";
import { type GenomeViewerState, type GenomeViewerStore, resetState } from "../store.ts";

export interface MainState {
  /** All tracks for all genomes. Knows in which group it belongs to */
  tracks: Tracks;
  /** A combination of genome + contig + some group settings. Contains a list of track ids */
  trackGroups: TrackGroups;
  /** The main HTML Canvas on the main thread */
  canvas?: HTMLCanvasElement;
  /** The main CanvasRenderingContext2D on the main thread */
  ctx?: CanvasRenderingContext2D;
  /** The currently hovered group id. Use between the main canvas, the tracks panel and the overview */
  hoveredGroupId?: string;
  hoveredTackId?: string;
  /** The currently loaded synteny organism */
  syntenyOrganism: {
    id: number | undefined;
    name: string | undefined;
    type: components["schemas"]["SequencingTypeEnum"] | undefined;
  };
  /** All genomes with all their contigs */
  organisms: Map<number, { id: number; name: string; contigs: Map<number, components["schemas"]["ContigResponse"]> }>;
  /** The alignment image is a big canvas / layer we receive from another worker */
  alignmentImageBitmap?: ImageBitmap;
  alignmentImageBitmapScrollX: number;
  alignmentImageBitmapScrollY: number;
  alignmentImageBitmapBasePerPixel: number;
  /** The alignment data but for the rendering (x, y, etc.). It's the aggregation from all tracks */
  orthologousGroups: OrthologousGroup[];
  /** Basic data for the currently active alignment */
  alignedFamily: MiniFamily | undefined;
  /** Chosen member to start the alignment */
  activeMemberId: number | undefined;
  /** "full" doesn't exist anymore. Too much alignments shown on screen */
  alignmentDensity: AlignmentDensity;
  pileupRenderingMode: PileupRenderingMode;
  /* Show any feature shape popover on right click */
  showFeaturePopover: boolean;
  /** Overview visible area box */
  isDraggingRegionIndicator: boolean;
  /** Must be complete. Is filled on shape hover + right click. */
  hoverData?: MainThreadHoverMessage;
  /** Data from the shape you are currently hovering, if any. If not, it can be the track only */
  contextualMenuData?: Partial<MainThreadHoverMessage>;
  /** Which synteny alignment type is currently active. Useful to know which tracks the lines are attached to. */
  activeSyntenyType?: SyntenyTypeEnum;
  asynchronousScrolling: boolean;
  /**
   * postRenderMessage -> +1 | handleImageBitmap -> -1
   * It's definitely weak if errors happen
   */
  forceOverviewRenderFlag: number;

  // Use track id for the key
  busyList: Set<string>;
  waitingList: Set<string>;
  showTrackHeaders: boolean;
  syntenyWorker: Worker | undefined;
  disableKeyboardShortcuts: boolean;
  needsRefresh: boolean;
  /** When navigating from the feature tables */
  featureToNavigate: NavigationFeature | undefined;
  /** When loading a view */
  positionToNavigate: CanvasCoordinate | undefined;
}

export interface MainActions {
  setReady(canvas: HTMLCanvasElement): void; // Canvas is in the DOM
  loadTracks: (orgId: number, initialContigs: number[], onError: (error: Error | FetchError) => void) => Promise<void>; // Load synteny atm
  /**
   * Add a new track in an existing group or a brand new group.
   * It will send the "render" message to the corresponding worker
   */
  createTracks(
    tracksToCreate: TrackConfig[],
    onError: (error: Error | FetchError) => void,
    afterGroupOrTrackId?: string,
  ): Set<{ trackId: string; groupId: string }>;
  updateTrack: (trackId: string, data: Partial<RenderingOptions>) => void;
  removeTracks(trackDeletions: { trackId: string; groupId: string }[], removeEmptyGroups?: boolean): void;
  setTrackGroupVisible: (trackGroupId: string, visible: boolean) => void;
  updateTrackContigId: (trackGroupId: string, contigId?: number, toBase?: number) => void;
  removeTrackGroups: (trackGroupIds: string[]) => void;
  reset: () => void;
  setAlignmentDensity(density: AlignmentDensity): void;
  resetAlignments: () => void;
  centerAlignment: () => void;
  toggleAsynchronousScrolling(): void;
  setPileupRenderingMode(pileupRenderingMode: PileupRenderingMode): void;
  forceOverviewRender(): void;
  toggleTrackHeaderVisibility(): void;
  isEveryTrackLoaded(): boolean;
}

export type MainSlice = MainActions & MainState;

export const initialMainState = {
  alignedFamily: undefined,
  activeMemberId: undefined,
  alignmentDensity: AlignmentDensityEnum.LOW,

  alignmentImageBitmap: undefined,
  alignmentImageBitmapBasePerPixel: 250,
  alignmentImageBitmapScrollX: 0,
  alignmentImageBitmapScrollY: 0,
  contextualMenuData: undefined,

  isDraggingRegionIndicator: false,

  organisms: new Map(),
  orthologousGroups: [],
  showFeaturePopover: false,
  syntenyOrganism: {
    id: undefined,
    name: undefined,
    type: undefined,
  },
  trackGroups: new Map<string, TrackGroup>(),
  tracks: new Map<string, Track>(),
  activeSyntenyType: undefined,
  asynchronousScrolling: false,
  pileupRenderingMode: PileupRenderingModeEnum.CONDENSED,
  forceOverviewRenderFlag: 0,
  busyList: new Set<string>(),
  waitingList: new Set<string>(),
  showTrackHeaders: true,
  syntenyWorker: undefined,
  disableKeyboardShortcuts: false,
  needsRefresh: true,
  featureToNavigate: undefined,
  positionToNavigate: undefined,
} satisfies MainState;

export const createMainSlice: StateCreator<GenomeViewerStore, [], [], MainSlice> = (set, get) => ({
  ...initialMainState,
  setReady(canvas) {
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.imageSmoothingEnabled = false;
    set({ canvas, ctx });
  },
  loadTracks: createTracksLoader(set, get),
  createTracks(tracksToCreate = [], onError, afterGroupOrTrackId) {
    const { tracks, trackGroups, organisms } = get();

    const { newTrackSet, ...storeUpdate } = createNewTracks(
      tracksToCreate,
      {
        organisms,
        trackGroups,
        tracks,
      },
      (w) => setupWorkerMessageHandler(w, set, get, onError),
      afterGroupOrTrackId,
    );

    set(storeUpdate);
    return newTrackSet;
  },
  updateTrack(trackId, data) {
    set(
      produce((state: GenomeViewerState) => {
        const track = state.tracks.get(trackId);
        if (track) {
          Object.assign(track, data);
        }
      }),
    );
    refreshTracks();
  },
  removeTracks(trackDeletions = [], removeEmptyGroups = false) {
    const { tracks, trackGroups, alignedFamily } = get();
    const update = removeTracks(trackDeletions, { trackGroups, tracks, alignedFamily }, removeEmptyGroups);

    set(update);
    refreshTracks();
  },
  removeTrackGroups(trackGroupIds) {
    const { trackGroups, removeTracks } = get();
    const trackDeletions = [];

    for (const groupId of trackGroupIds) {
      const trackIds = trackGroups.get(groupId)?.trackIds ?? [];

      // Terminate worker and all tracks from the group
      for (const trackId of trackIds) {
        trackDeletions.push({ trackId, groupId });
      }
    }
    if (trackDeletions.length) {
      removeTracks(trackDeletions, true);
    }
    refreshTracks();
  },
  setTrackGroupVisible(trackGroupId, visible) {
    set(
      produce((state: GenomeViewerState) => {
        const group = state.trackGroups.get(trackGroupId);
        if (group) {
          group.graphics.isVisible = visible;
        }
      }),
    );
    refreshTracks();
  },
  updateTrackContigId(trackGroupId, contigId, toBase) {
    const { alignedFamily, trackGroups, organisms, tracks } = get();
    const trackGroup = trackGroups.get(trackGroupId);

    if (!trackGroup) return;

    const organism = organisms.get(trackGroup.organismId);

    if (!organism) return;

    const contig = contigId ? organism.contigs.get(contigId) : organism.contigs.values().next().value;

    if (!contig) return;

    trackGroup.graphics.length = contig.length;

    trackGroups.set(trackGroupId, {
      ...trackGroup,
      contigId: contig.id,
      contigName: contig.name,
      graphics: {
        ...trackGroup.graphics,
        baseShift: toBase ?? trackGroup.graphics.baseShift,
      },
    });

    for (const trackId of trackGroup.trackIds) {
      tracks.set(trackId, {
        ...tracks.get(trackId)!,
        isLoaded: false,
      });
    }

    if (alignedFamily) {
      alignedFamily.members = alignedFamily.members.filter((member) => !trackGroup.trackIds.includes(member.trackId));
    }

    set({
      alignedFamily,
      trackGroups: new Map(trackGroups),
      tracks,
      needsRefresh: true,
    });
  },
  reset() {
    const { tracks, ctx, alignmentImageBitmap, syntenyWorker } = get();

    for (const track of tracks.values()) {
      track.imageBitmap?.close();
      track.worker.terminate();
    }

    alignmentImageBitmap?.close();
    syntenyWorker?.terminate();

    ctx?.reset();

    set({
      ...resetState,
    });
  },
  setAlignmentDensity(alignmentDensity) {
    set({ alignmentDensity });
    refreshTracks();
  },
  resetAlignments() {
    const trackGroups = get().trackGroups;
    for (const trackGroup of trackGroups) {
      trackGroup[1].graphics.baseShift = 0;
    }

    set({
      alignedFamily: undefined,
      currentlyAlignedTrackGroupIds: undefined,
      trackGroups: new Map(trackGroups),
    });
    refreshTracks();
  },
  centerAlignment() {
    const { alignedFamily, basePerPixel, canvas, orthologousGroups, trackGroups, tracks } = get();
    if (canvas == undefined || alignedFamily == undefined) {
      return;
    }

    const alignedOrthologs = orthologousGroups
      .find((group) => group.orthologId === alignedFamily.familyId)
      ?.orthologs.filter((o) => alignedFamily.members.some((ortho) => ortho.featureId === o.id));

    if (alignedOrthologs === undefined) {
      return;
    }

    const minMemberStartX = Math.min(
      ...alignedOrthologs.map(
        (ortholog) =>
          (ortholog.start + trackGroups.get(tracks.get(ortholog.trackId)!.groupId)!.graphics.baseShift) / basePerPixel,
      ),
    );
    const maxMemberStopX = Math.max(
      ...alignedOrthologs.map(
        (ortholog) =>
          (ortholog.stop + trackGroups.get(tracks.get(ortholog.trackId)!.groupId)!.graphics.baseShift) / basePerPixel,
      ),
    );

    const maxFamilyWidth = maxMemberStopX - minMemberStartX;
    let scale = 1;
    if (maxFamilyWidth >= canvas.width || maxFamilyWidth <= MIN_FOCUSED_SHAPE_WIDTH) {
      scale = (2 * maxFamilyWidth) / canvas.width;
    }

    const newScrollX = minMemberStartX / scale - (canvas.width - maxFamilyWidth / scale) / 2;

    set({
      scrollX: newScrollX,
      basePerPixel: basePerPixel * scale,
    });
    refreshTracks();
  },
  toggleAsynchronousScrolling() {
    set({
      asynchronousScrolling: !get().asynchronousScrolling,
    });
  },
  setPileupRenderingMode(pileupRenderingMode) {
    set({ orthologousGroups: [] });
    set({ pileupRenderingMode });
    refreshTracks();
  },
  forceOverviewRender() {
    set((state) => ({
      forceOverviewRenderFlag: state.forceOverviewRenderFlag + 1,
    }));
  },
  toggleTrackHeaderVisibility() {
    set({
      showTrackHeaders: !get().showTrackHeaders,
    });
    refreshMain();
  },
  isEveryTrackLoaded() {
    const activeSyntenyType = get().activeSyntenyType;
    const trackArr = [...get().tracks.values()].filter(
      (track) => track.isVisible && get().trackGroups.get(track.groupId)?.graphics.isVisible,
    );

    return (
      trackArr.length > 0 &&
      trackArr.every((track) => track.isLoaded) &&
      trackArr.every((track) =>
        track.trackType === getTrackTypeBySyntenyType(activeSyntenyType!) ? track.isOrthologsLoaded : true,
      )
    );
  },
});
